/**
 * Game-session latch.
 *
 * A live game must stay pinned to the session it launched with. `PlayGameUnity` derives the game iframe from
 * the shared upcoming-activity query (`useSessionId` → `useGetUpcomingActivityForGamer`, staleTime 0), which is
 * refetched constantly while playing (the realtime sync invalidates it on every session write). If the backend
 * stops resolving the game mid-play — e.g. a premature `/Session/Close` flips the game session to `completed`,
 * so the resolver returns the reflection (or nothing) instead — the derived `sessionId` changes and, without a
 * latch, the iframe `src` changes or the loading-spinner branch unmounts the iframe. Either tears the player
 * out of the Photon room; every player at once = the whole team kicked mid-loop.
 *
 * These pure helpers encode the latch: once the game is ready (not waiting for a session) we capture its URL,
 * and from then on the running game ignores any later change in the resolved session. The game may only end via
 * Unity's own `onGameEnded` / `onGameAborted` callbacks, or an operator's Back Office close, which stamps
 * `Session.forceClosedAt` (`useLeaveGameWhenForceClosed`).
 */

import { isReflectionActivityType } from '../../../../../pure-js/libs/isAIDebriefActivity'

/** Next latched URL: keep the existing latch; otherwise latch once we're no longer waiting for a session. */
export const computeLatchedGameUrl = (
  currentLatched: string | null,
  shouldWaitForSession: boolean,
  gameUrl: string
): string | null => {
  if (currentLatched) return currentLatched
  return shouldWaitForSession ? null : gameUrl
}

/** Show the loading spinner ONLY before the first launch. Once latched, the live game stays mounted. */
export const shouldBlockOnSessionSpinner = (latchedGameUrl: string | null, shouldWaitForSession: boolean): boolean =>
  !latchedGameUrl && shouldWaitForSession

/** The URL the iframe actually renders: the latched one once set, else the freshly-derived one. */
export const resolveEffectiveGameUrl = (latchedGameUrl: string | null, gameUrl: string): string =>
  latchedGameUrl ?? gameUrl

/**
 * Every configured loop has been played, so nothing may launch — the game reads 0 as "no fixed count"
 * and would run for ever. `undefined` means "not known yet", which is not "finished".
 */
export const hasPlayedEveryConfiguredLoop = (params: {
  numberOfLoops: number | undefined
  /** Only a looped game carries a count; a tutorial or a reflection must never be closed by this. */
  isLoopedGame: boolean
}): boolean => params.isLoopedGame && params.numberOfLoops === 0

/** The team has moved on to the reflection after its game, so the game page has no game left to run. */
export const hasTeamMovedOnToReflection = (params: {
  activityType: string | undefined
  /** A player's own game page; a tutorial and a facilitator's page run their game regardless. */
  isPlayerGamePage: boolean
}): boolean => params.isPlayerGamePage && isReflectionActivityType(params.activityType)

/** Leave a game call only when exhaustion blocked launch; never interrupt an already-latched Unity run. */
export const shouldReleaseCallForExhaustedGame = (
  hasPlayedEveryLoop: boolean,
  latchedGameUrl: string | null
): boolean => hasPlayedEveryLoop && !latchedGameUrl
